import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import {
  changeChatTicket,
  chatCommandsQuery,
  chatUsersQuery,
  fetchNewChatMessages,
  inviteToChat,
  suggestChatAction,
  type ChatNewMessage,
  type ChatPanel,
  type ChatRecommendedAction,
} from '../api/chatSupport'
import { sendTicketChat, ticketChatQuery, type TicketChatMessage, type TicketChatPage } from '../api/easeedesk'
import { rcSendOperations } from '../api/easeedeskRemoteControl'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import './ChatSupport.css'

/**
 * Bucatile comune ale celor doua chaturi ale operatorului - fila "Chat" din fisa
 * (`$.chat`, `chat_conversation.js`) si panoul ecranului de suport
 * (`$.quick_chat_support`, `quick_chat_support.js`). Portalul le scria de doua
 * ori, aproape la fel; aici o singura data, cu aceleasi clase:
 *
 *   useChatPolling        `newMessages`: la 10 secunde, `getNewMessages/{id}`;
 *                         ce vine se lipeste la firul din cache (`ticketChatQuery`),
 *                         iar actiunile recomandate se dau inapoi
 *   ChatMessageList       `messagesList` / `listContent`: `li.my-message`,
 *                         `other-message`, `host-message`, `guest-message`
 *   ChatSendForm          `sendMessageForm`: casuta si "Send"; Enter trimite,
 *                         Ctrl+Enter rand nou
 *   ChatCannedMessages    `textButton`: meniul "Add canned messages"
 *   ChatRecommendations   `recommendationsArea` + `showRecommendedActions`
 *   ChatChangeTicketButton `changeChatTicketButton`: "Change to <tip>"
 *   InviteUserModal       `#inviteChatUsersModal` (modals.php)
 *   SuggestActionModal    `#suggestNewActionModal` (modals.php)
 */

/* ------------------------------------------------------------------------ */
/*  Ora                                                                      */
/* ------------------------------------------------------------------------ */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/**
 * `moment(created).format('H:mm, MMMM Do')`: "9:01, September 1st".
 *
 * Ora e cea din tabela (GMT, cum o scrie `Chat_messages::saveMessage`), la fel
 * cum o arata fila "Chat" de la inceput; portalul o muta pe server in fusul
 * din profil. Mutarea in fusul omului ramane de facut o data, pentru toate
 * cele trei file de mesaje.
 */
export function chatStamp(at: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(at)
  if (!m) return at
  const day = Number(m[3])
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th'
  return `${Number(m[4])}:${m[5]}, ${MONTHS[Number(m[2]) - 1]} ${day}${suffix}`
}

/** `ticketStatusType == 1`: numai pe un status activ se scrie; pana vine capul, se scrie. */
export function chatClosed(panel: ChatPanel | undefined): boolean {
  return panel !== undefined && panel.status_type > 1
}

/* ------------------------------------------------------------------------ */
/*  Mesajele noi, la 10 secunde                                              */
/* ------------------------------------------------------------------------ */

export const CHAT_POLL_MS = 10000

/**
 * `newMessages()` din cele doua scripturi: la fiecare 10 secunde se cer
 * mesajele necitite ale clientului. Serverul le marcheaza citite si spune
 * aparatului (`CHR`); aici se lipesc la fir si se dau actiunile recomandate.
 */
export function useChatPolling(
  id: number,
  enabled: boolean,
  onRecommended: (actions: ChatRecommendedAction[]) => void,
  /** Au venit mesaje noi (deci serverul le-a marcat citite) - fila Chat isi reciteste contorul. */
  onFresh?: (count: number) => void,
): void {
  const client = useQueryClient()
  const latest = useRef(onRecommended)
  latest.current = onRecommended
  const fresher = useRef(onFresh)
  fresher.current = onFresh

  useEffect(() => {
    if (!enabled) return
    let stopped = false
    const tick = async () => {
      try {
        const fresh = await fetchNewChatMessages(id)
        if (stopped) return
        if (fresh.messages.length > 0) {
          client.setQueryData<TicketChatPage>(ticketChatQuery(id).queryKey, (have) => {
            if (!have) return have
            const known = new Set(have.messages.map((m) => m.id))
            const add = fresh.messages.filter((m) => !known.has(m.id))
            return add.length === 0 ? have : { ...have, messages: [...have.messages, ...add] }
          })
          fresher.current?.(fresh.messages.length)
        }
        latest.current(fresh.recommended_actions)
      } catch {
        /* Un tact pierdut nu opreste ceasul: urmatorul vine peste 10 secunde. */
      }
    }
    const timer = setInterval(() => void tick(), CHAT_POLL_MS)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [id, enabled, client])
}

/* ------------------------------------------------------------------------ */
/*  Firul                                                                    */
/* ------------------------------------------------------------------------ */

type AnyChatMessage = TicketChatMessage | ChatNewMessage

function sourceOf(m: AnyChatMessage): number {
  return 'source' in m ? m.source : 0
}

/**
 * `listContent` din `quick_chat_support.js`: clientul (`user_type = 2`) sta in
 * stanga (`my-message`, sau `guest-message` cand vine de pe portalul de
 * oaspete), operatorul in dreapta (`other-message` / `host-message`). Culorile
 * vin din setarile chatului firmei (`received_message_color`,
 * `sent_message_color`); fara ele, cele din script.
 */
export function ChatMessageList({
  messages,
  showAuthor,
  colors,
}: {
  messages: AnyChatMessage[]
  showAuthor: boolean
  colors?: ChatPanel['chat_settings']
}) {
  if (messages.length === 0) {
    return (
      <ul>
        <li>
          <div className="note note-info tk-msg-empty">
            <i className="fas fa-comment" aria-hidden="true"></i> No messages!
          </div>
        </li>
      </ul>
    )
  }

  return (
    <ul>
      {messages.map((message) => {
        const guest = sourceOf(message) === 1
        if (message.kind === 'agent') {
          const color = guest ? '#32777E' : colors?.received_message_color || '#3AACB3'
          return (
            <li key={message.id} className={guest ? 'host-message tk-chatc-agent' : 'other-message tk-chatc-agent'}>
              <div className="message-data">
                <span className="message-data-time">{chatStamp(message.at)}</span>
                {showAuthor && <span className="message-data-name">{message.author}</span>}
                <i className={guest ? 'fas fa-circle offline-host' : 'fas fa-circle me'} style={{ color }} aria-hidden="true"></i>
              </div>
              <div className="message sent tk-chatc-body" style={{ background: color, borderRightColor: color }}>
                {message.message}
              </div>
            </li>
          )
        }
        const color = guest ? '#B4B4B4' : colors?.sent_message_color || '#575757'
        return (
          <li key={message.id} className={guest ? 'guest-message tk-chatc-customer' : 'my-message tk-chatc-customer'}>
            <div className="message-data">
              <i className={guest ? 'fas fa-circle online-guest' : 'fas fa-circle online'} style={{ color }} aria-hidden="true"></i>
              {showAuthor && <span className="message-data-name">{guest ? 'GUEST' : message.author}</span>}
              <span className="message-data-time">{chatStamp(message.at)}</span>
            </div>
            <div className="message received tk-chatc-body" style={{ background: color, borderLeftColor: color }}>
              {message.message}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/* ------------------------------------------------------------------------ */
/*  Casuta de trimis                                                         */
/* ------------------------------------------------------------------------ */

/**
 * `sendMessageForm` / `textareaField` / `formButtons`: casuta si "Send" in
 * dreapta; Enter trimite, Ctrl+Enter rand nou (ca in portal) - si Shift+Enter,
 * pe care il incearca toata lumea si care pana acum TRIMITEA mesajul. Textul sta la parinte, ca
 * mesajele predefinite si cele recomandate sa poata scrie in el
 * (`a.predefined-message`, `.recommended-message`).
 */
export function ChatSendForm({
  id,
  text,
  onText,
  compact = false,
}: {
  id: number
  text: string
  onText(text: string): void
  /** Panoul de suport: casuta pe un rand, butonul doar cu avionul. */
  compact?: boolean
}) {
  const client = useQueryClient()

  const send = useMutation({
    mutationFn: () => sendTicketChat(id, text),
    onSuccess: () => {
      onText('')
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'chat'] })
    },
  })

  const submit = () => {
    if (text.trim() === '' || send.isPending) return
    send.mutate()
  }

  const onKey = (ev: KeyboardEvent<HTMLTextAreaElement>) => {
    if (ev.key !== 'Enter') return
    /* Shift+Enter: randul nou il pune casuta singura. */
    if (ev.shiftKey) return
    if (ev.ctrlKey) {
      onText(text + '\n')
      ev.preventDefault()
      return
    }
    ev.preventDefault()
    submit()
  }

  return (
    <div className="messaging-message clearfix tk-chatc-send">
      <form
        className="send_chat_message"
        method="post"
        onSubmit={(ev) => {
          ev.preventDefault()
          submit()
        }}
      >
        <input type="hidden" name="ticket_id" value={id} />
        <table className="tk-chatc-send-table">
          <tbody>
            <tr>
              <td>
                <textarea
                  className="form-control"
                  name="message"
                  rows={compact ? 1 : 2}
                  placeholder="Type your message"
                  aria-label="Type your message"
                  value={text}
                  onChange={(ev) => onText(ev.target.value)}
                  onKeyDown={onKey}
                ></textarea>
              </td>
              <td className="tk-chatc-send-buttons">
                <button type="submit" className="btn btn-primary btn-sm send-message" disabled={text.trim() === '' || send.isPending}>
                  <i className="fa fa-paper-plane" aria-hidden="true"></i>
                  {!compact && ' Send'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        {send.isError && <p className="text-danger tk-chatc-error">{(send.error as Error).message}</p>}
      </form>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Uneltele de sub casuta                                                   */
/* ------------------------------------------------------------------------ */

/**
 * `textButton`: "Add canned messages" - un meniu in sus; clicul lipeste mesajul
 * la text. `variant="button"`: un buton mic langa casuta (fila Chat din fisa),
 * in loc de bara pe toata latimea (panoul de suport).
 */
export function ChatCannedMessages({
  messages,
  onPick,
  variant = 'bar',
}: {
  messages: { id: number; message: string }[]
  onPick(message: string): void
  variant?: 'bar' | 'act'
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (ev: MouseEvent) => {
      if (root.current && !root.current.contains(ev.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  const menu = (
    <ul className="dropdown-menu dropdown-menu-right tk-chat-canned-menu">
      {messages.length === 0 && (
        <li>
          <a className="tk-chat-canned-none">No canned messages</a>
        </li>
      )}
      {messages.map((m) => (
        <li key={m.id}>
          <a
            href="#canned"
            className="predefined-message"
            onClick={(ev) => {
              ev.preventDefault()
              onPick(m.message)
              setOpen(false)
            }}
          >
            {m.message}
          </a>
        </li>
      ))}
    </ul>
  )

  /* Unealta mica de sub casuta filei Chat din tichet, ca uneltele lui Adam
     (`od-ai-act`, din `public/css/od_ai.css`). */
  if (variant === 'act') {
    return (
      <div ref={root} className={`dropup tk-chat-canned-btn${open ? ' open' : ''}`}>
        <button
          type="button"
          className="od-ai-act tkc-act dropdown-toggle add-canned-message"
          title="Insert a canned message"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <i className="far fa-comment-alt" aria-hidden="true"></i> <span className="tkc-act-label">Canned</span>
          {messages.length > 0 && <span className="od-ai-cmd-badge tkc-badge tk-chat-canned-count">{messages.length}</span>}
        </button>
        {menu}
      </div>
    )
  }

  return (
    <div ref={root} className={`btn-group dropup tk-chat-canned${open ? ' open' : ''}`}>
      <div
        className="dropdown-toggle add-canned-message"
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault()
            setOpen((o) => !o)
          }
        }}
      >
        <div>
          <i className="fas fa-plus" aria-hidden="true"></i> <strong className="tk-chat-canned-label">Add canned messages</strong>
        </div>{' '}
        <i className="fas fa-chevron-down tk-chat-canned-caret" aria-hidden="true"></i>
      </div>
      {menu}
    </div>
  )
}

/**
 * `changeChatTicketButton` + `button.change-chat-ticket`: discutia devine un
 * tichet de tipul din setari; intreaba o data (swal in portal), apoi butonul
 * dispare.
 */
export function ChatChangeTicketButton({
  id,
  change,
  onDone,
  variant = 'button',
}: {
  id: number
  change: NonNullable<ChatPanel['change_ticket']>
  onDone?(newTicket: number): void
  /* `act`: unealta mica de sub casuta din fila Chat a tichetului. */
  variant?: 'button' | 'act'
}) {
  const [gone, setGone] = useState(false)
  const run = useMutation({
    mutationFn: () => changeChatTicket(id, change.type_id, change.status_id),
    onSuccess: (reply) => {
      setGone(true)
      toast('Ticket changed', 'Success', 'success')
      if (!reply.chatTicketMoved && reply.chatTicketMessage !== '') {
        toast(reply.chatTicketMessage, 'The chat ticket was not moved', 'warning')
      }
      onDone?.(reply.newTicket)
    },
    onError: (err: Error) => toast(err.message || 'Ticket could not be changed', 'Error changing the ticket', 'error'),
  })
  if (gone) return null

  return (
    <button
      type="button"
      className={variant === 'act' ? 'od-ai-act tkc-act tkc-act-warn change-chat-ticket tk-chat-change' : 'btn btn-danger change-chat-ticket tk-chat-change'}
      title={`Change this ticket to ${change.type_name}`}
      data-ticket={id}
      disabled={run.isPending}
      onClick={() => {
        if (window.confirm('Are you sure you want to change this ticket? You will not be able to make any actions on this one.')) run.mutate()
      }}
    >
      <i className="fa fa-ticket-alt" aria-hidden="true"></i> {variant === 'act' ? <span className="tkc-act-label">Change to {change.type_name}</span> : <>Change to {change.type_name}</>}
    </button>
  )
}

/**
 * `recommendationsArea` + `showRecommendedActions`: sub casuta, pe fond
 * albastru, actiunile pe care le-a potrivit serverul pe ultimul mesaj al
 * clientului - mesaje (clicul le pune in casuta) si comenzi (clicul le
 * trimite aparatului prin Remote Control, ca `init-cmd`), plus butonul "+"
 * (`suggest-new-action`), care deschide fereastra de sugestie.
 */
export function ChatRecommendations({
  id,
  device,
  actions,
  onPickMessage,
  onSuggest,
  bare = false,
}: {
  id: number
  device: number
  actions: ChatRecommendedAction[]
  onPickMessage(message: string): void
  onSuggest(): void
  /** Numai actiunile, fara coloanele si fara "+" (fila Chat il are langa casuta). */
  bare?: boolean
}) {
  const send = useMutation({
    mutationFn: (a: { cmdId: number; args: string }) =>
      rcSendOperations(id, device, [{ cmdId: a.cmdId, type: 'CMD', informClient: 0, customerConfirmation: 0, arguments: a.args }]),
    onSuccess: () => toast('Instruction sent', 'Commands', 'success'),
    onError: (err: Error) => toast(err.message, 'Commands', 'error'),
  })

  return (
    <div className={bare ? 'tk-chat-reco tk-chat-reco-bare' : 'tk-chat-reco'}>
      <div className={bare ? 'recommendations tk-chat-reco-list' : 'col-md-11 recommendations tk-chat-reco-list'}>
        {actions.map((action) => {
          if (action.action_type === 'message') {
            const words = action.message.split(' ')
            return (
              <span
                key={`m${action.id}`}
                className="label label-primary recommended-message tk-chat-reco-message"
                title={action.message}
                data-value={action.message}
                role="button"
                tabIndex={0}
                onClick={() => onPickMessage(action.message)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') onPickMessage(action.message)
                }}
              >
                {words.slice(0, 3).join(' ')}
                {words.length > 3 ? '...' : ''}
              </span>
            )
          }
          if (device < 1) return null
          return (
            <span key={`c${action.id}`} className="tk-chat-reco-command">
              <button
                type="button"
                className="init-cmd btn btn-xs btn-danger"
                data-cmd={action.command_id}
                data-device={device}
                data-ticket={id}
                disabled={send.isPending}
                onClick={() => {
                  let args = ''
                  if (action.has_parameters === 1) {
                    const asked = window.prompt(action.display_name, '')
                    if (asked === null) return
                    args = asked
                  }
                  send.mutate({ cmdId: action.command_id, args })
                }}
              >
                {action.display_name}
              </button>
              {action.info !== '' && <i className="fas fa-info-circle color-blue font-14 tk-chat-reco-info" title={action.info} aria-hidden="true"></i>}
            </span>
          )
        })}
      </div>
      {!bare && (
        <div className="col-md-1 tk-chat-reco-add">
          <button type="button" className="suggest-new-action" title="Suggest new action" data-ticket={id} onClick={onSuggest}>
            <i className="fas fa-plus-circle font-20 color-steel" aria-hidden="true"></i>
          </button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Ferestrele                                                               */
/* ------------------------------------------------------------------------ */

/** `#inviteChatUsersModal`: colegii, cu bulina verde la cei activi; "Invite" scrie la clopotelul lui. */
export function InviteUserModal({ id, onClose }: { id: number; onClose(): void }) {
  const users = useQuery(chatUsersQuery)
  const [user, setUser] = useState('')
  const invite = useMutation({
    mutationFn: () => inviteToChat(id, Number(user)),
    onSuccess: () => {
      toast('User invited.', 'Success', 'success')
      onClose()
    },
    onError: (err: Error) => toast(err.message || 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="inviteChatUsersModal" open onClose={onClose} title="Invite user to chat" labelledBy="inviteChatUsersModalLabel">
      <form
        id="inviteChatUserForm"
        method="post"
        onSubmit={(ev) => {
          ev.preventDefault()
          if (user !== '') invite.mutate()
        }}
      >
        <input type="hidden" name="ticket_id" value={id} />
        <div className="form-group">
          <SelectPicker
            id="inviteChatUser"
            className="form-control input-sm"
            name="user_id"
            title="Select user"
            size={10}
            value={user}
            onChange={setUser}
            options={(users.data ?? []).map((u) => ({
              value: String(u.value),
              label: u.name,
              icon: `fas fa-circle${u.status === 1 ? ' color-green' : ''}`,
            }))}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" id="inviteUser" disabled={user === '' || invite.isPending}>
            Invite
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * `#suggestNewActionModal`: cuvintele-cheie (portalul le umplea cu ultimul
 * mesaj al clientului), mesajele (randuri care se adauga cu "+"), comenzile din
 * catalog; "Add" scrie actiunea in asteptarea aprobarii.
 */
export function SuggestActionModal({ id, keywords: initial, onClose }: { id: number; keywords: string; onClose(): void }) {
  const commands = useQuery(chatCommandsQuery)
  const [keywords, setKeywords] = useState(initial)
  const [messages, setMessages] = useState<string[]>([''])
  const [picked, setPicked] = useState<string[]>([])
  const add = useMutation({
    mutationFn: () => suggestChatAction(id, keywords, messages.map((m) => m.trim()).filter((m) => m !== ''), picked.map(Number)),
    onSuccess: () => {
      toast('Action suggested.', 'Success', 'success')
      onClose()
    },
    onError: (err: Error) => toast(err.message || 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="suggestNewActionModal" open onClose={onClose} title="Suggest new action" labelledBy="suggestNewActionModalLabel">
      <form
        id="suggestNewActionForm"
        method="post"
        onSubmit={(ev) => {
          ev.preventDefault()
          if (keywords.trim() !== '') add.mutate()
        }}
      >
        <input type="hidden" name="ticket_id" value={id} />
        <div className="form-group">
          <label htmlFor="suggestKeywords" className="control-label">
            Keywords
          </label>
          <input id="suggestKeywords" type="text" className="form-control" name="keywords" value={keywords} onChange={(ev) => setKeywords(ev.target.value)} />
        </div>
        <div className="form-group">
          <label className="control-label">Messages</label>
          {messages.map((m, i) => (
            <div key={i} className="entry input-group tk-chat-suggest-entry">
              <input
                type="text"
                className="form-control"
                name="messages[]"
                aria-label={`Message ${i + 1}`}
                value={m}
                onChange={(ev) => setMessages((have) => have.map((x, k) => (k === i ? ev.target.value : x)))}
              />
              <span className="input-group-btn">
                {i === messages.length - 1 ? (
                  <button className="btn btn-success btn-add" type="button" title="Add message" onClick={() => setMessages((have) => [...have, ''])}>
                    <i className="fas fa-plus" aria-hidden="true"></i>
                  </button>
                ) : (
                  <button className="btn btn-danger btn-remove" type="button" title="Remove message" onClick={() => setMessages((have) => have.filter((_, k) => k !== i))}>
                    <i className="fas fa-minus" aria-hidden="true"></i>
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="form-group">
          <label className="control-label">Cmd commands</label>
          <SelectPicker
            id="suggestCommands"
            className="form-control input-sm"
            name="commands[]"
            title={commands.data && commands.data.length === 0 ? 'No cmd commands found' : 'Cmd commands'}
            size={10}
            multiple
            value={picked}
            onChange={setPicked}
            options={(commands.data ?? []).map((c) => ({ value: String(c.id), label: c.display_name }))}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" id="addNewSuggestion" disabled={keywords.trim() === '' || add.isPending}>
            Add
          </button>
        </div>
      </form>
    </Modal>
  )
}
